import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useConfirmShowcaseCheckout, useShowcase, useShowcaseCheckout, useShowcasePortal } from "@/hooks/useShowcase";
import "./ShowcaseBilling.scss";

export const PRICE_LINE = "$59 a month";

const dateOf = (value) =>
  value ? new Date(value).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";

export const billingStateOf = (showcase, now = Date.now()) => {
  if (!showcase) return "loading";
  if (showcase.comped) return "comped";
  const status = showcase.billing?.status || showcase.status;
  if (status === "paused") return "paused";
  if (status === "active") return "active";
  if (status === "past_due") {
    const grace = showcase.billing?.graceUntil;
    return grace && new Date(grace).getTime() > now ? "grace" : "lapsed";
  }
  if (status === "canceled") return "canceled";
  return "none";
};

const doneLineOf = (checklist) => {
  if (checklist?.live) return "You\u2019re subscribed. Your showcase is live on your\u00a0website.";
  if (checklist?.siteAdded) return "You\u2019re subscribed. Next, put the code on your site so the showcase can\u00a0load.";
  return "You\u2019re subscribed. Next, add your website address so the showcase can load\u00a0there.";
};

const go = (url) => window.location.assign(url);

const ShowcaseBilling = ({ brokerageId, checkout, onCheckoutHandled, onGo }) => {
  const query = useShowcase(brokerageId);
  const startCheckout = useShowcaseCheckout(brokerageId);
  const portal = useShowcasePortal(brokerageId);
  const confirm = useConfirmShowcaseCheckout(brokerageId);
  const [result, setResult] = useState(null);
  const confirmedFor = useRef(null);

  useEffect(() => {
    if (!checkout) return;
    if (checkout.outcome === "canceled") {
      setResult("canceled");
      onCheckoutHandled?.();
      return;
    }
    if (!checkout.sessionId || confirmedFor.current === checkout.sessionId) return;
    confirmedFor.current = checkout.sessionId;
    setResult("confirming");
    confirm.mutate(checkout.sessionId, {
      onSuccess: () => setResult("done"),
      onError: () => setResult("failed"),
      onSettled: () => onCheckoutHandled?.(),
    });
  }, [checkout?.sessionId, checkout?.outcome]);

  const showcase = query.data?.showcase;
  if (!showcase) {
    return query.isError ? (
      <div className="showcase-billing__state">
        Billing did not load.{" "}
        <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => query.refetch()}>
          Try again
        </button>
      </div>
    ) : (
      <div className="showcase-billing__state" aria-busy="true">
        Loading billing…
      </div>
    );
  }

  const billing = showcase.billing || {};
  const state = billingStateOf(showcase);
  const canManage = billing.canManageBilling !== false;

  const subscribe = () =>
    startCheckout.mutate(undefined, {
      onSuccess: (data) => (data?.url ? go(data.url) : toast.error("Checkout did not open. Please try again.")),
      onError: (error) => toast.error(error.message),
    });

  const manage = () =>
    portal.mutate(undefined, {
      onSuccess: (data) => (data?.url ? go(data.url) : toast.error("Billing did not open. Please try again.")),
      onError: (error) => toast.error(error.message),
    });

  const manageLink = canManage ? (
    <button type="button" className="showcase-billing__link" disabled={portal.isPending} onClick={manage}>
      {portal.isPending ? "Opening…" : "Manage billing"}
    </button>
  ) : (
    <span>{billing.payerName ? `${billing.payerName} manages billing.` : "Another manager handles billing."}</span>
  );

  const planLine =
    state === "comped"
      ? "Included by Gitcha, at no charge to your office"
      : state === "active"
        ? [
            PRICE_LINE,
            billing.currentPeriodEnd ? `renews ${dateOf(billing.currentPeriodEnd)}` : "renews monthly",
            billing.payerName ? `billed to ${billing.payerName}` : "",
          ]
            .filter(Boolean)
            .join(" · ")
        : null;

  const statusLine =
    state === "grace"
      ? `Your last payment did not go through. Your showcase stays on your site until ${dateOf(billing.graceUntil)}; update your card before then.`
      : state === "lapsed"
        ? "Your showcase is hidden on your site until the payment goes through. Your order and pins are kept."
        : state === "paused"
          ? "Gitcha paused your showcase. It is hidden on your site and you are not being charged."
          : state === "canceled"
            ? `Your subscription ended${billing.currentPeriodEnd ? ` on ${dateOf(billing.currentPeriodEnd)}` : ""}. Subscribe again and it comes back with your order and pins.`
            : null;

  return (
    <section className="content-block showcase-billing">
      <div className="title-sec">
        <h4>Website Showcase</h4>
      </div>

      {result === "confirming" ? (
        <p className="showcase-billing__notice" role="status">
          Confirming your subscription with&nbsp;Stripe…
        </p>
      ) : null}
      {result === "done" ? (
        <p className="showcase-billing__notice is-good" role="status">
          {doneLineOf(showcase?.checklist)}{" "}
          {showcase?.checklist?.live ? null : (
            <button type="button" className="showcase-billing__link" onClick={() => onGo("setup")}>
              Finish setup
            </button>
          )}
        </p>
      ) : null}
      {result === "failed" ? (
        <p className="showcase-billing__notice is-bad" role="alert">
          We could not confirm your payment yet. If Stripe charged your card, it shows here within a few minutes.{" "}
          <button
            type="button"
            className="showcase-billing__link"
            onClick={() => {
              setResult("confirming");
              confirm.mutate(confirmedFor.current, {
                onSuccess: () => setResult("done"),
                onError: () => setResult("failed"),
              });
            }}
          >
            Check again
          </button>
        </p>
      ) : null}
      {result === "canceled" ? (
        <p className="showcase-billing__notice" role="status">
          Checkout closed. Nothing was&nbsp;charged.
        </p>
      ) : null}

      {planLine ? (
        <div className="showcase-billing__row">
          <span className="showcase-billing__label">Plan</span>
          <span className="showcase-billing__value">
            {planLine}
            {state === "active" ? manageLink : null}
          </span>
        </div>
      ) : statusLine ? (
        <div className="showcase-billing__row">
          <span className="showcase-billing__label">Status</span>
          <span className="showcase-billing__value">
            {statusLine}
            {state === "paused" ? (
              <a href="mailto:support@gitcha.com">Email support</a>
            ) : state === "canceled" ? null : (
              manageLink
            )}
          </span>
        </div>
      ) : null}

      {state === "none" || state === "canceled" ? (
        <div className="showcase-billing__offer">
          <p>
            Your agents&apos; public Buyer Want&#8209;Listings on your own website, in your order. Sellers who see a fit
            reach the agent, credited to your brokerage. Up to 5 websites plus a hosted page on Gitcha.com, no setup
            fee, cancel any&nbsp;time.
          </p>
          <button
            type="button"
            className="btn btn-primary showcase-billing__button"
            disabled={startCheckout.isPending}
            onClick={subscribe}
          >
            {startCheckout.isPending ? "Opening checkout…" : `Subscribe for ${PRICE_LINE}`}
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default ShowcaseBilling;
